import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
/** design-spec §4 Tooltip: 10 left of the trigger's content, 10 below it, 280 wide. */
const GAP = 10;
const OFFSET = `${GAP}px`;
/**
 * Whether a focus came from the keyboard rather than a press. `:focus-visible` is the
 * browser's own heuristic; where a selector engine does not know it (jsdom), a focus counts
 * as keyboard focus.
 */
function keyboardFocus(element) {
    try {
        return element.matches(":focus-visible");
    }
    catch {
        return true;
    }
}
export function Tooltip({ title, detail, children }) {
    const id = useId();
    const [open, setOpen] = useState(false);
    const [above, setAbove] = useState(false);
    const anchor = useRef(null);
    const tip = useRef(null);
    useLayoutEffect(() => {
        if (!open || !anchor.current || !tip.current)
            return;
        const bottom = anchor.current.getBoundingClientRect().bottom;
        // The gap plus the tooltip's own height, measured while it is still placed below.
        setAbove(bottom + GAP + tip.current.offsetHeight > window.innerHeight);
    }, [open]);
    // While open: Esc closes it wherever focus is (a hover-opened tooltip included), and a press
    // outside it closes it, which is the only close a tap gets where a tap never focuses.
    useEffect(() => {
        if (!open)
            return;
        const onKey = (event) => {
            if (event.key === "Escape")
                setOpen(false);
        };
        const onPress = (event) => {
            if (!anchor.current?.contains(event.target))
                setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("pointerdown", onPress, true);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("pointerdown", onPress, true);
        };
    }, [open]);
    const mouse = (event) => event.pointerType === "mouse";
    return (_jsxs("span", { ref: anchor, "data-tooltip-root": "", style: { position: "relative", display: "inline-block" }, children: [_jsx("button", { type: "button", "data-tooltip-trigger": "", "data-open": open ? "" : undefined, "aria-describedby": id, onPointerEnter: (event) => mouse(event) && setOpen(true), onPointerLeave: (event) => {
                    if (mouse(event) && document.activeElement !== event.currentTarget)
                        setOpen(false);
                }, onFocus: (event) => keyboardFocus(event.currentTarget) && setOpen(true), onBlur: () => setOpen(false), onClick: () => setOpen((was) => !was), onKeyDown: (event) => {
                    if (event.key === "Escape" && open) {
                        event.stopPropagation();
                        setOpen(false);
                    }
                }, style: {
                    display: "inline-flex", alignItems: "center",
                    margin: "calc(-1 * var(--space-025)) calc(-1 * var(--space-075))",
                    padding: "var(--space-025) var(--space-075)",
                    border: 0, borderRadius: "var(--radius-focus)",
                    background: "var(--tooltip-trigger-bg, transparent)",
                    color: "inherit", font: "inherit", textAlign: "inherit", cursor: "help",
                    transition: "background var(--dur-fast) var(--ease-out)",
                }, children: children }), _jsxs("span", { ref: tip, id: id, role: "tooltip", "data-tooltip": "", "data-placement": above ? "above" : "below", style: {
                    position: "absolute", zIndex: 20, left: `-${OFFSET}`,
                    ...(above ? { bottom: `calc(100% + ${OFFSET})` } : { top: `calc(100% + ${OFFSET})` }),
                    width: 280, padding: `${OFFSET} var(--space-15)`,
                    borderRadius: "var(--radius-panel)", background: "var(--tip-bg)", boxShadow: "var(--shadow-tooltip)",
                    fontSize: "var(--type-small-size)", lineHeight: 1.45, textAlign: "left", whiteSpace: "normal",
                    pointerEvents: "none",
                    opacity: open ? 1 : 0, visibility: open ? "visible" : "hidden",
                    transition: "opacity var(--dur-fast) var(--ease-out), visibility var(--dur-fast) var(--ease-out)",
                }, children: [_jsx("span", { "aria-hidden": true, style: {
                            position: "absolute", left: 22, width: 10, height: 10, background: "var(--tip-bg)",
                            transform: "rotate(45deg)", ...(above ? { bottom: -5 } : { top: -5 }),
                        } }), _jsx("span", { "data-tooltip-title": "", style: { position: "relative", display: "block", color: "var(--tip-ink)", fontWeight: "var(--type-body-weight-current)" }, children: title }), _jsx("span", { "data-tooltip-detail": "", style: {
                            position: "relative", display: "block", marginTop: "var(--space-05)",
                            color: "var(--tip-ink-2)", fontWeight: "var(--type-small-weight)",
                        }, children: detail })] })] }));
}
